import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, User, Menu } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearch } from '../contexts/SearchContext';
import { useModal } from '../contexts/ModalContext';
import { generateMovieUrl } from '../utils/urlUtils';

const HeaderNav = ({
  masterItems = [],
  userCollectionItems = [],
  wantlistItemsForSaleCount = 0,
  unreadMarketplaceCount = 0,
  unreadCommunityCount = 0,
  userProfile,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAdmin, openAuthModal, handleLogout } = useAuth();
  const search = useSearch();
  const modal = useModal();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [, setShowAdminAccess] = useState(false);
  const [showAllDesktopResults, setShowAllDesktopResults] = useState(false);
  const [showAllMobileResults, setShowAllMobileResults] = useState(false);
  const dropdownRef = useRef(null);

  // Check for admin access parameter or key combination
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasAdminParam = urlParams.get('admin') === 'true';
    const hasAdminAccess = localStorage.getItem('admin_access') === 'true';
    if (hasAdminParam || hasAdminAccess) {
      setShowAdminAccess(true);
      localStorage.setItem('admin_access', 'true');
    }

    // Listen for key combinations
    const handleKeyDown = e => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        setShowAdminAccess(true);
        localStorage.setItem('admin_access', 'true');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      // Check if click is outside both the button and the dropdown
      const isClickInsideButton = dropdownRef.current && dropdownRef.current.contains(event.target);
      const isClickInsideDropdown = event.target.closest('.profile-dropdown');

      if (!isClickInsideButton && !isClickInsideDropdown) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Use server-side search results
  const filteredSearchItems = search.globalSearchResults || [];

  const SEARCH_FIELDS = [
    { value: 'all', label: 'All' },
    { value: 'title', label: 'Title' },
    { value: 'director', label: 'Director' },
    { value: 'actor', label: 'Actor' },
    { value: 'year', label: 'Year' },
    { value: 'genre', label: 'Genre' },
  ];

  const FieldChips = () => (
    <div className="flex gap-1.5 px-3 py-2 border-b border-gray-700 overflow-x-auto scrollbar-hide">
      {SEARCH_FIELDS.map(f => (
        <button
          key={f.value}
          onMouseDown={e => e.preventDefault()}
          onClick={() => search.setFilterCategory(f.value)}
          className={`px-2.5 py-1 text-xs rounded-full border whitespace-nowrap transition-colors flex-shrink-0 ${
            search.filterCategory === f.value
              ? 'bg-red-600 border-red-600 text-white'
              : 'border-gray-600 text-gray-400 hover:border-red-500 hover:text-white'
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );

  // Reset expand state when search query changes
  useEffect(() => {
    setShowAllDesktopResults(false);
    setShowAllMobileResults(false);
  }, [search.searchQuery]);

  return (
    <header className="bg-black text-white shadow-sm border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-auto md:h-32 flex flex-col justify-between py-2 md:py-4">
          {/* Top Row - Logo, Search, and Mobile Menu */}
          <div className="flex justify-between items-center space-x-2">
            {/* Logo - smaller on mobile */}
            <button
              onClick={() => navigate('/')}
              className="text-lg md:text-2xl font-bold hover:opacity-80 transition-opacity flex-shrink-0"
            >
              <span className="text-white hover:text-red-500 tracking-widest">
                R e w i n d • A r c h i v e
              </span>
            </button>

            {/* Desktop Search Bar and Add Button - hidden on mobile */}
            <div className="hidden md:flex items-center space-x-3 flex-1 max-w-2xl">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search for anything...add if not found →"
                  value={search.searchQuery}
                  onChange={e => search.setSearchQuery(e.target.value)}
                  onFocus={() => search.setShowSearchResults(true)}
                  onBlur={e => {
                    // Don't close if clicking inside the dropdown
                    if (!e.relatedTarget || !e.relatedTarget.closest('.search-dropdown')) {
                      setTimeout(() => search.setShowSearchResults(false), 200);
                    }
                  }}
                  className="pl-10 pr-4 py-2 border border-gray-600 rounded-lg bg-black text-white placeholder-gray-400 hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none w-full transition-colors"
                />

                {/* Search Results Dropdown */}
                {search.showSearchResults && search.searchQuery && (
                  <div className="search-dropdown absolute top-full left-0 right-0 bg-black border border-gray-600 rounded-lg shadow-xl mt-2 z-50 max-h-96 overflow-y-auto dark-scrollbar">
                    <FieldChips />
                    <div className="p-2">
                      {!search.searchQuery ? null : search.isGlobalSearching ? (
                        <div className="text-center py-4">
                          <div className="text-gray-400">Searching...</div>
                        </div>
                      ) : filteredSearchItems.length > 0 ? (
                        <>
                          <div className="text-xs text-gray-500 px-3 py-2">
                            {filteredSearchItems.length} results found
                          </div>
                          {(showAllDesktopResults
                            ? filteredSearchItems
                            : filteredSearchItems.slice(0, 8)
                          ).map(item => (
                            <button
                              key={item.id}
                              onClick={() => {
                                const url = generateMovieUrl(item, 'catalog');
                                navigate(url);
                                search.setShowSearchResults(false);
                                search.setSearchQuery('');
                              }}
                              className="w-full px-3 py-3 text-left hover:border hover:border-red-500 flex items-center space-x-3 border-b border-gray-600 last:border-b-0 transition-all border border-transparent rounded-lg"
                            >
                              {/* VHS Cover */}
                              <div className="w-12 h-16 bg-gray-100 rounded flex-shrink-0 flex items-center justify-center">
                                {item.cover_image_url ? (
                                  <img
                                    src={item.cover_image_url}
                                    alt=""
                                    className="w-full h-full object-cover rounded"
                                  />
                                ) : (
                                  <div className="text-lg">📼</div>
                                )}
                              </div>

                              {/* Movie Info */}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-white truncate">
                                  {item.title} ({item.year})
                                </div>
                                <div className="text-sm text-gray-300 truncate">
                                  {item.director && `Directed by ${item.director}`}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {item.genre} {item.studio && `• ${item.studio}`}
                                </div>
                              </div>
                            </button>
                          ))}

                          {filteredSearchItems.length > 8 && !showAllDesktopResults && (
                            <button
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => setShowAllDesktopResults(true)}
                              className="w-full p-2 bg-black text-red-500 hover:text-red-400 border-t border-gray-600 transition-colors text-sm"
                            >
                              + {filteredSearchItems.length - 8} more
                            </button>
                          )}

                          {showAllDesktopResults && filteredSearchItems.length > 8 && (
                            <button
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => setShowAllDesktopResults(false)}
                              className="w-full p-2 bg-black text-red-500 hover:text-red-400 border-t border-gray-600 transition-colors text-sm"
                            >
                              Show less
                            </button>
                          )}
                        </>
                      ) : (
                        /* No Results Found */
                        <div className="p-4 text-center">
                          <div className="text-gray-500 mb-3">
                            No results found for "{search.searchQuery}"
                          </div>
                          <button
                            onClick={() => {
                              const searchTerm = search.searchQuery;
                              search.setSearchQuery('');
                              search.setShowSearchResults(false);
                              // First open the Add VHS modal, then open MovieSearchModal with the search term
                              modal.openModal('addMaster');
                              setTimeout(() => {
                                modal.openModal('movieSearch', { initialSearchTerm: searchTerm });
                              }, 100);
                            }}
                            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <Search size={16} className="mr-2" />
                            Search for "{search.searchQuery}" and add to catalog
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Add VHS Button */}
              {currentUser && (
                <button
                  onClick={() => {
                    modal.openAddMasterModal(null, false);
                  }}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-1 whitespace-nowrap"
                >
                  <Plus size={16} className="md:hidden" />
                  <span>Add Title</span>
                </button>
              )}

              {/* Desktop Avatar - moved here */}
              {currentUser ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => {
                      console.log(
                        'Desktop profile button clicked, current state:',
                        showProfileDropdown
                      );
                      setShowProfileDropdown(!showProfileDropdown);
                    }}
                    className="text-white hover:text-red-500 p-1 rounded-full transition-colors relative"
                    title="Profile"
                  >
                    {userProfile?.avatar_url ? (
                      <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-black hover:border-red-500 transition-colors">
                        <img
                          src={userProfile.avatar_url}
                          alt={`${userProfile.username}'s profile`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <User size={20} />
                    )}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => openAuthModal('login')}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2 whitespace-nowrap"
                >
                  Login • Sign Up
                </button>
              )}
            </div>

            {/* Mobile Controls */}
            <div className="flex md:hidden items-center space-x-2">
              {/* Mobile Add Button */}
              {currentUser && (
                <button
                  onClick={() => {
                    modal.openAddMasterModal(null, false);
                  }}
                  className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700"
                >
                  <Plus size={18} />
                </button>
              )}

              {/* Mobile Login/User Button */}
              {currentUser ? (
                <button
                  onClick={() => {
                    console.log(
                      'Mobile profile button clicked, current state:',
                      showProfileDropdown
                    );
                    setShowProfileDropdown(!showProfileDropdown);
                  }}
                  className="text-white hover:text-red-500 p-1 rounded-full transition-colors relative"
                  title="Profile"
                >
                  {userProfile?.avatar_url ? (
                    <div className="w-6 h-6 rounded-full overflow-hidden border-2 border-black hover:border-red-500 transition-colors">
                      <img
                        src={userProfile.avatar_url}
                        alt={`${userProfile.username}'s profile`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <User size={18} />
                  )}
                </button>
              ) : (
                <button
                  onClick={() => openAuthModal('login')}
                  className="text-white hover:text-gray-300 p-2 rounded-lg hover:bg-gray-700"
                  title="Login"
                >
                  <User size={18} />
                </button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white p-2 rounded-lg hover:bg-gray-700"
                title="Menu"
              >
                <Menu size={18} />
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          <div className="md:hidden mt-3 mb-2">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search..."
                value={search.searchQuery}
                onChange={e => search.setSearchQuery(e.target.value)}
                onFocus={() => search.setShowSearchResults(true)}
                onBlur={e => {
                  // Don't close if clicking inside the dropdown
                  if (!e.relatedTarget || !e.relatedTarget.closest('.search-dropdown')) {
                    setTimeout(() => search.setShowSearchResults(false), 200);
                  }
                }}
                className="pl-9 pr-4 py-2 border border-gray-600 rounded-lg bg-black text-white placeholder-gray-400 hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none w-full transition-colors"
              />
              {/* Mobile Search Results Dropdown */}
              {search.showSearchResults && search.searchQuery && (
                <div className="search-dropdown absolute top-full left-0 right-0 bg-black border border-gray-600 rounded-lg shadow-xl mt-2 z-50 max-h-96 overflow-y-auto dark-scrollbar">
                  <FieldChips />
                  <div className="p-2">
                    {!search.searchQuery ? null : search.isGlobalSearching ? (
                      <div className="text-center py-4">
                        <div className="text-gray-400 text-sm">Searching...</div>
                      </div>
                    ) : filteredSearchItems.length > 0 ? (
                      <>
                        <div className="text-xs text-gray-500 px-3 py-2">
                          {filteredSearchItems.length} results found
                        </div>
                        {(showAllMobileResults
                          ? filteredSearchItems
                          : filteredSearchItems.slice(0, 8)
                        ).map(item => (
                          <button
                            key={item.id}
                            onClick={() => {
                              const url = generateMovieUrl(item, 'catalog');
                              navigate(url);
                              search.setShowSearchResults(false);
                              search.setSearchQuery('');
                            }}
                            className="w-full px-3 py-3 text-left hover:border hover:border-red-500 flex items-center space-x-3 border-b border-gray-600 last:border-b-0 transition-all border border-transparent rounded-lg"
                          >
                            <div className="w-8 h-10 bg-gray-700 rounded flex-shrink-0 flex items-center justify-center">
                              {item.cover_image_url ? (
                                <img
                                  src={item.cover_image_url}
                                  alt=""
                                  className="w-full h-full object-cover rounded"
                                />
                              ) : (
                                <div className="text-sm">📼</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-white truncate text-sm">
                                {item.title} ({item.year})
                              </div>
                              <div className="text-xs text-gray-300 truncate">
                                {item.director && `${item.director}`}
                              </div>
                            </div>
                          </button>
                        ))}

                        {filteredSearchItems.length > 8 && !showAllMobileResults && (
                          <button
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => setShowAllMobileResults(true)}
                            className="w-full p-2 bg-black text-red-500 hover:text-red-400 border-t border-gray-600 transition-colors text-sm"
                          >
                            + {filteredSearchItems.length - 8} more
                          </button>
                        )}

                        {showAllMobileResults && filteredSearchItems.length > 8 && (
                          <button
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => setShowAllMobileResults(false)}
                            className="w-full p-2 bg-black text-red-500 hover:text-red-400 border-t border-gray-600 transition-colors text-sm"
                          >
                            Show less
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="p-4 text-center">
                        <div className="text-gray-300 mb-3 text-sm">
                          No results found for "{search.searchQuery}"
                        </div>
                        <button
                          onClick={() => {
                            const searchTerm = search.searchQuery;
                            search.setSearchQuery('');
                            search.setShowSearchResults(false);
                            modal.openModal('addMaster');
                            setTimeout(() => {
                              modal.openModal('movieSearch', { initialSearchTerm: searchTerm });
                            }, 100);
                          }}
                          className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                          <Search size={14} className="mr-2" />
                          Search and add to catalog
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex justify-between items-end overflow-hidden">
            {/* Left Side - Navigation Tabs */}
            <nav className="flex space-x-6 overflow-x-auto scrollbar-hide flex-shrink min-w-0 -ml-2">
              <button
                onClick={() => navigate('/catalog')}
                className={`px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm font-medium rounded-md whitespace-nowrap ${
                  location.pathname.startsWith('/catalog')
                    ? 'text-red-500'
                    : 'text-white hover:text-red-500'
                }`}
              >
                Full Catalog
              </button>
              <button
                onClick={() => navigate('/collection')}
                className={`px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm font-medium rounded-md whitespace-nowrap ${
                  location.pathname.startsWith('/collection')
                    ? 'text-red-500'
                    : 'text-white hover:text-red-500'
                }`}
              >
                My Collection
              </button>
              <button
                onClick={() => navigate('/wantlist')}
                className={`px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm font-medium rounded-md whitespace-nowrap ${
                  location.pathname.startsWith('/wantlist')
                    ? 'text-red-500'
                    : 'text-white hover:text-red-500'
                }`}
              >
                <div className="flex items-center space-x-1">
                  <span>Wantlist</span>
                  {wantlistItemsForSaleCount > 0 && (
                    <span className="text-xs bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                      {wantlistItemsForSaleCount}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => navigate('/marketplace')}
                className={`px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm font-medium rounded-md whitespace-nowrap ${
                  location.pathname.startsWith('/marketplace')
                    ? 'text-red-500'
                    : 'text-white hover:text-red-500'
                }`}
              >
                <div className="flex items-center space-x-1">
                  <span>Marketplace</span>
                  {unreadMarketplaceCount > 0 && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              </button>
              <button
                onClick={() => navigate('/community')}
                className={`px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm font-medium rounded-md whitespace-nowrap ${
                  location.pathname.startsWith('/community')
                    ? 'text-red-500'
                    : 'text-white hover:text-red-500'
                }`}
              >
                <div className="flex items-center space-x-1">
                  <span>Community</span>
                  {unreadCommunityCount > 0 && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              </button>
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className={`px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm font-medium rounded-md whitespace-nowrap ${
                    location.pathname.startsWith('/admin')
                      ? 'text-red-500'
                      : 'text-white hover:text-red-500'
                  }`}
                >
                  <div className="flex items-center space-x-1">
                    <span>Admin Portal</span>
                    <span className="text-xs bg-red-600 text-white rounded px-1">ADMIN</span>
                  </div>
                </button>
              )}
            </nav>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-3 mb-2 bg-black rounded-lg p-3 shadow-lg border border-gray-600">
              <nav className="space-y-2">
                <button
                  onClick={() => {
                    navigate('/catalog');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md ${
                    location.pathname.startsWith('/catalog')
                      ? 'text-red-500'
                      : 'text-white hover:text-red-500'
                  }`}
                >
                  Full Catalog
                </button>
                <button
                  onClick={() => {
                    navigate('/collection');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md ${
                    location.pathname.startsWith('/collection')
                      ? 'text-red-500'
                      : 'text-white hover:text-red-500'
                  }`}
                >
                  My Collection
                </button>
                <button
                  onClick={() => {
                    navigate('/wantlist');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md ${
                    location.pathname.startsWith('/wantlist')
                      ? 'text-red-500'
                      : 'text-white hover:text-red-500'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>Wantlist</span>
                    {wantlistItemsForSaleCount > 0 && (
                      <span className="text-xs bg-red-500 text-white rounded-full px-2 py-0.5">
                        {wantlistItemsForSaleCount}
                      </span>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => {
                    navigate('/marketplace');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md ${
                    location.pathname.startsWith('/marketplace')
                      ? 'text-red-500'
                      : 'text-white hover:text-red-500'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>Marketplace</span>
                    {unreadMarketplaceCount > 0 && (
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => {
                    navigate('/community');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md ${
                    location.pathname.startsWith('/community')
                      ? 'text-red-500'
                      : 'text-white hover:text-red-500'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>Community</span>
                    {unreadCommunityCount > 0 && (
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    )}
                  </div>
                </button>
                {isAdmin && (
                  <button
                    onClick={() => {
                      navigate('/admin');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md ${
                      location.pathname.startsWith('/admin')
                        ? 'text-red-500'
                        : 'text-white hover:text-red-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Admin Portal</span>
                      <span className="text-xs bg-red-600 text-white rounded px-1">ADMIN</span>
                    </div>
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Profile Dropdown - Rendered outside header to avoid clipping */}
      {showProfileDropdown && (
        <div
          className="profile-dropdown fixed top-20 right-4 w-56 bg-black rounded-lg shadow-xl border border-gray-600"
          style={{ zIndex: 99999 }}
        >
          {userProfile ? (
            <>
              <div className="px-4 py-3 border-b border-gray-600">
                <p className="text-sm font-medium text-white">Hi {userProfile.username}!</p>
              </div>
              <button
                onClick={() => {
                  console.log('Navigating to profile:', userProfile.username);
                  navigate(`/users/${userProfile.username}`);
                  setShowProfileDropdown(false);
                }}
                className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-red-500 hover:bg-black flex items-center space-x-2 border-b border-gray-600"
              >
                <span>View Profile</span>
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setShowProfileDropdown(false);
                }}
                className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-red-500 hover:bg-black flex items-center space-x-2 rounded-b-lg"
              >
                <span>Logout</span>
              </button>
            </>
          ) : (
            <div className="px-4 py-3 text-gray-300">Loading...</div>
          )}
        </div>
      )}
    </header>
  );
};

export default HeaderNav;
